import { Ref, useCallback, useMemo } from "react";
import cx from "classnames";

import Box from "ds/components/Box";
import {
  Column,
  ColumnId,
  ColumnMode,
  RenderItemContentProps,
} from "components/DragDropList/types";
import Tile from "ds/components/Tile";
import Input from "ds/components/Input";
import FormField from "ds/components/Form/Field";
import Toggle from "ds/components/Toggle";
import DragDropList from "components/DragDropList";
import DropEmptyState from "components/DropEmptyState";
import { ArrowBoxColored } from "components/icons/generated";
import useIsLearnViewAvailable from "hooks/useIsLearnViewAvailable";
import useTypedFlags from "hooks/useTypedFlags";
import { AccountContext } from "views/AccountWrapper";
import useTypedContext from "hooks/useTypedContext";
import Card from "ds/components/Card";
import DragDropListDefaultItem from "components/DragDropList/DefaultItem";

import {
  DashboardColumns,
  DashboardConfig,
  DashboardOverviewStaticColumn,
  OverviewStaticWidget,
  Widget,
} from "../types";
import styles from "./styles.module.css";
import { DEFAULT_TAB_IDS, DefaultDashboardTabs, OVERVIEW_STATIC_WIDGETS } from "../constants";
import { getAllDashboardWidgets } from "../helpers";
import DashboardManageDrawerTabItemTitle from "./TabItemTitle";
import { DASHBOARD_MANAGE_DRAWER_EMPTY_CONTAINER_OFFSET } from "./constants";

type DashboardManageDrawerTabProps = {
  tab: string;
  inputRef?: Ref<HTMLInputElement>;
  managementDrawerConfig: DashboardConfig;
  updateManagementDrawerTabColumnsConfig: (tab: string, columns: DashboardColumns) => void;
  updateManagementDrawerTabColumnModeConfig: (tab: string, columns: ColumnMode) => void;
  updateManagementDrawerTabOverviewColumnConfig: (column: DashboardOverviewStaticColumn) => void;
  changeTabName: (id: string, value: string) => void;
};

const DashboardManageDrawerTab = ({
  tab,
  inputRef,
  managementDrawerConfig,
  updateManagementDrawerTabColumnsConfig,
  updateManagementDrawerTabColumnModeConfig,
  updateManagementDrawerTabOverviewColumnConfig,
  changeTabName,
}: DashboardManageDrawerTabProps) => {
  const item = managementDrawerConfig[tab];
  const { left, right } = item;

  const {
    numberOfDeploymentsWidgetFrontend,
    approvalPolicyEvaluationsWidgetFrontend,
    opentofuBackend,
  } = useTypedFlags();

  const isLearnViewAvailable = useIsLearnViewAvailable();

  const { viewer } = useTypedContext(AccountContext);

  const widgetsRaw = useMemo(
    () =>
      getAllDashboardWidgets({
        numberOfDeploymentsWidgetFrontend,
        approvalPolicyEvaluationsWidgetFrontend,
        opentofuBackend,
      }),
    [numberOfDeploymentsWidgetFrontend, approvalPolicyEvaluationsWidgetFrontend, opentofuBackend]
  );

  const overviewToggles = useMemo(
    () =>
      (item.overviewColumn ?? []).filter((widget) => {
        if (isLearnViewAvailable && widget.value === OverviewStaticWidget.LaunchPad) return false;
        if (!isLearnViewAvailable && widget.value === OverviewStaticWidget.Learn) return false;
        return true;
      }),
    [item.overviewColumn, isLearnViewAvailable]
  );

  const onSelect = useCallback(
    (columnId: ColumnId, id: Widget, isSelected: boolean) => {
      const side = columnId === ColumnId.First ? "left" : "right";
      const updatedItem = { ...item };
      updatedItem[side] = updatedItem[side].map((item) =>
        item.value === id ? { ...item, hidden: !isSelected } : item
      );

      updateManagementDrawerTabColumnsConfig(tab, updatedItem);
    },
    [item, updateManagementDrawerTabColumnsConfig, tab]
  );

  const renderItemContent = useCallback(
    ({
      id,
      dragging,
      handleRef,
      handleListeners,
      handleAttributes,
      columnId,
      disabled,
      props,
    }: RenderItemContentProps<Widget, { title: string; checked: boolean }>) => {
      return (
        <DragDropListDefaultItem
          dragging={dragging}
          handleRef={handleRef}
          handleListeners={handleListeners}
          handleAttributes={handleAttributes}
          disabled={disabled}
          titleComponent={
            <DashboardManageDrawerTabItemTitle
              title={props.title}
              checked={props.checked}
              onSelect={onSelect}
              columnId={columnId}
              id={id as Widget}
            />
          }
        />
      );
    },
    [onSelect]
  );

  const onChange = ({
    first,
    second,
  }: {
    first: Column<Widget, { checked: boolean }>;
    second: Column<Widget, { checked: boolean }>;
  }) => {
    updateManagementDrawerTabColumnsConfig(tab, {
      left: first.map((item) => ({
        value: item.value,
        hidden: !item.props?.checked,
      })),
      right: second.map((item) => ({
        value: item.value,
        hidden: !item.props?.checked,
      })),
    });
  };

  const handleColumnModeChange = (columnMode: ColumnMode) => {
    updateManagementDrawerTabColumnModeConfig(tab, columnMode);
  };

  const handleOverviewWidgetVisibility = (value: OverviewStaticWidget, hidden: boolean) => {
    if (managementDrawerConfig["overview"].overviewColumn) {
      updateManagementDrawerTabOverviewColumnConfig(
        managementDrawerConfig["overview"].overviewColumn?.map((item) =>
          item.value === value ? { ...item, hidden } : item
        )
      );
    }
  };

  const titleError =
    !DEFAULT_TAB_IDS.includes(tab) && !item.title ? "Title is required." : undefined;

  const renderEmptyColumnState = useCallback(
    (isDragActive: boolean) => (
      <DropEmptyState
        padding="large"
        icon={ArrowBoxColored}
        caption="Drag & Drop a widget from other column"
        dropCaption="You can drop the widget here"
        isDragActive={isDragActive}
      />
    ),
    []
  );

  const firstColumn = useMemo(() => {
    return left.map((item) => ({
      value: item.value,
      props: {
        checked: !item.hidden,
        title: widgetsRaw[item.value]?.title || "",
      },
    }));
  }, [left, widgetsRaw]);

  const secondColumn = useMemo(() => {
    return right.map((item) => ({
      value: item.value,
      props: {
        checked: !item.hidden,
        title: widgetsRaw[item.value]?.title || "",
      },
    }));
  }, [right, widgetsRaw]);

  return (
    <Box direction="column" grow="1" fullWidth>
      {!DEFAULT_TAB_IDS.includes(tab) && (
        <Card variant="secondary">
          <FormField error={titleError} label="Title" fullWidth noMargin>
            {({ ariaInputProps }) => (
              <Input
                placeholder="Type view title here"
                error={!!titleError}
                ref={inputRef as React.RefObject<HTMLInputElement>}
                value={item.title}
                onChange={(e) => changeTabName(tab, e.target.value)}
                {...ariaInputProps}
              />
            )}
          </FormField>
        </Card>
      )}
      <Box gap="x-large" padding={!DEFAULT_TAB_IDS.includes(tab) ? "x-large 0 0 0" : undefined}>
        <Tile
          title="Full width layout"
          selected={item.columnMode === ColumnMode.Single}
          indicator="radio"
          onClick={() => handleColumnModeChange(ColumnMode.Single)}
        >
          <Box padding="large" margin="medium 0 0 0" className={cx(styles.columnMode, styles.full)}>
            <div className={styles.cell} />
            <div className={styles.cell} />
          </Box>
        </Tile>
        <Tile
          title="Two columns layout"
          selected={item.columnMode === ColumnMode.Double}
          indicator="radio"
          onClick={() => handleColumnModeChange(ColumnMode.Double)}
        >
          <Box
            padding="large"
            margin="medium 0 0 0"
            className={cx(styles.columnMode, styles.double)}
          >
            <div className={styles.cell} />
            <div className={styles.cell} />
            <div className={styles.cell} />
            <div className={styles.cell} />
          </Box>
        </Tile>
      </Box>
      {tab === DefaultDashboardTabs.Overview && !!overviewToggles.length && viewer.admin && (
        <Box direction="column" gap="x-large" padding="x-large 0 0 0">
          {overviewToggles.map((item) => (
            <Toggle
              key={item.value}
              isSelected={!item.hidden}
              onChange={(isSelected) => handleOverviewWidgetVisibility(item.value, !isSelected)}
              variant="switch"
            >
              {`Show ${OVERVIEW_STATIC_WIDGETS[item.value]}`}
            </Toggle>
          ))}
        </Box>
      )}
      {item && (
        <Box direction="column" gap="x-large" padding="x-large 0 0 0">
          <DragDropList
            renderItemContent={renderItemContent}
            onChange={onChange}
            onSelect={onSelect}
            columnMode={item.columnMode}
            firstColumn={firstColumn}
            secondColumn={secondColumn}
            firstColumnTitle={item.columnMode === ColumnMode.Double ? "Column 1" : undefined}
            secondColumnTitle="Column 2"
            renderEmptyColumnState={renderEmptyColumnState}
            vertical
            verticalAxisOnly
            emptyContainerOffset={DASHBOARD_MANAGE_DRAWER_EMPTY_CONTAINER_OFFSET}
            hasDroppableCardWrapper
          />
        </Box>
      )}
    </Box>
  );
};

export default DashboardManageDrawerTab;
